import { MAX_PAGE_SIZE, type MediaKind, type PlatformMediaAsset } from '@aysar/contracts';
import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectionKeyboard } from '@/shared/components/selection/useSelectionKeyboard/useSelectionKeyboard';
import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { type MediaSource } from '../types/media-source.types';

/** The first page. Two doublings reach the api's hard ceiling. */
export const MEDIA_LIBRARY_PAGE_SIZE = 24;

export interface UseMediaLibraryBodyLogicInput {
  kind: MediaKind;
  source: MediaSource;
}

export interface UseMediaLibraryBodyLogic {
  items: readonly PlatformMediaAsset[];
  /** The tiles as selection options — the grid's keyboard reads these. */
  options: readonly SelectionOption[];
  isFetching: boolean;
  isError: boolean;
  isEmpty: boolean;
  canLoadMore: boolean;
  canDelete: boolean;
  loadMore: () => void;
  retry: (() => void) | undefined;
  remove: (mediaId: string) => Promise<void>;
  /** The roving tab stop, from the selection family (one keyboard, not two). */
  activeValue: string | null;
  gridRef: (node: HTMLDivElement | null) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * The library grid's paging, its keyboard and its deletions.
 *
 * **"Load more" WIDENS the page** — 24 → 48 → 96 → 100 — rather than appending
 * a page to a list held in state. That is `useServerOptions`'s design and it is
 * here for the same three reasons: appending inside an effect is a
 * `set-state-in-effect` violation (an error in this workspace); the answer is
 * always the complete, correctly ordered list, so offset paging over a table
 * being written to cannot show a row twice; and RTK Query caches each width, so
 * the widened fetch is one request while the rows already on screen stay put.
 *
 * **Nothing is removed optimistically.** `deletePlatformMedia` invalidates the
 * list tag, so the api's own answer takes the row off screen — a local removal
 * that has to be put back when the delete is refused is a flicker that reads as
 * a bug.
 *
 * **The keyboard is the selection family's**, with `isGrid` on. A second
 * implementation of arrow movement is how `MultiSelect` and `ServerSelect`
 * drifted into two unrelated controls before P10.2a put them back together.
 *
 * There is no `isOpen` here at all: the body is mounted only while the dialog is
 * open, so closing it resets the widened page by unmounting — no effect, and no
 * reopening on page 3 of a library the operator has not looked at since.
 */
export function useMediaLibraryBodyLogic({
  kind,
  source,
}: UseMediaLibraryBodyLogicInput): UseMediaLibraryBodyLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const [pageSize, setPageSize] = useState(MEDIA_LIBRARY_PAGE_SIZE);

  const result = source.useLibrary({ kind, page: 1, pageSize });

  const options = useMemo<SelectionOption[]>(
    () =>
      result.items.map((item) => ({
        value: item.id,
        // The tile shows a picture; this is the name a screen reader reads, and
        // the dimensions are the only thing that distinguishes two logos.
        //
        // Through `formatNumber`, exactly as the picker's own facts line is:
        // otherwise "1200" is read out beside a sibling control that says
        // "1,200" — two spellings of one fact inside one control.
        label: t('media.dimensions', {
          width: formatNumber(item.width ?? 0, locale),
          height: formatNumber(item.height ?? 0, locale),
        }),
      })),
    [locale, result.items, t],
  );

  const { activeValue, listRef, onKeyDown } = useSelectionKeyboard({ options, isGrid: true });

  const loadMore = useCallback(() => {
    setPageSize((current) => Math.min(current * 2, MAX_PAGE_SIZE));
  }, []);

  const remove = useCallback(
    async (mediaId: string) => {
      // The api is the authority and its invalidation takes the row off screen.
      // A refusal is the caller's to surface; the row simply stays.
      await source.remove?.(mediaId);
    },
    [source],
  );

  return {
    items: result.items,
    options,
    isFetching: result.isFetching,
    isError: result.isError,
    // An error is NOT an empty library: "nothing has been uploaded yet" is a
    // claim there is no evidence for when the request itself failed.
    isEmpty: !result.isFetching && !result.isError && result.items.length === 0,
    canLoadMore: result.hasNext && pageSize < MAX_PAGE_SIZE,
    canDelete: source.remove !== undefined,
    loadMore,
    retry: result.refetch,
    remove,
    activeValue,
    gridRef: listRef,
    onKeyDown,
  };
}
